// Lazy loader for Cloudflare Turnstile, the signup bot check.
//
// Loaded on /signup only, at runtime, never from public/index.html: that file
// has no external <script> tags (src/publicIndexHtml.test.ts), and a widget
// used on one page must not put a request on every page. Same rule as
// paddleLoader.ts and analytics.ts.
//
// Components use the global this resolves with, never window.turnstile.
//
// The tag is injected once and the load promise memoized. A failed load
// clears the promise and removes its tag, so the next mount retries from
// scratch instead of replaying the failure until a full page reload.
//
// Explicit render mode, and no turnstile.ready(): Cloudflare documents that
// ready() throws when the script is loaded async, which this always is.
//
// The widget will not render until the CSP allows challenges.cloudflare.com
// in script-src and frame-src. That lives in template.yaml and is served by
// CloudFront, so `npm start` (which serves no CSP) shows nothing about it.
// See backend/tests/unit/test_template_csp.py.

import { TURNSTILE_SCRIPT_SRC } from '../constants';

export interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  callback?: (token: string) => void;
  'error-callback'?: (code?: string) => void;
  'expired-callback'?: () => void;
}

export interface TurnstileGlobal {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string | undefined;
  reset: (widgetId?: string) => void;
  remove: (widgetId?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}

let loadPromise: Promise<TurnstileGlobal> | null = null;

const injectScript = (): Promise<TurnstileGlobal> =>
  new Promise((resolve, reject) => {
    if (window.turnstile) {
      resolve(window.turnstile);
      return;
    }

    const onLoad = () => {
      if (window.turnstile) {
        resolve(window.turnstile);
      } else {
        reject(new Error('Security check script loaded but exposed no global'));
      }
    };
    const onError = () => reject(new Error('Security check script failed to load'));

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${TURNSTILE_SCRIPT_SRC}"]`
    );
    if (existing) {
      // A tag still in flight with no promise to await, e.g. one left behind
      // by resetTurnstileLoaderForTests. A failed tag never reaches here:
      // loadTurnstile removes it on rejection.
      existing.addEventListener('load', onLoad);
      existing.addEventListener('error', onError);
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onError);
    document.head.appendChild(script);
  });

export const loadTurnstile = (): Promise<TurnstileGlobal> => {
  if (!loadPromise) {
    // Either rejection spends the tag, so it goes with the promise.
    loadPromise = injectScript().catch((err: unknown) => {
      loadPromise = null;
      document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`)?.remove();
      throw err;
    });
  }
  return loadPromise;
};

// Test seam. The module-level memoization is the point in a browser and a
// liability across tests.
export const resetTurnstileLoaderForTests = (): void => {
  loadPromise = null;
};
